import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   * Toggles between minus and delete icon when at minimum quantity
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // At min: show delete icon, keep button enabled for removal
    if (value <= min) {
      this.setAttribute('data-at-min', '');
      minusButton.disabled = false;
    } else {
      this.removeAttribute('data-at-min');
      minusButton.disabled = false;
    }

    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Handles the quantity decrease event.
   * When at minimum quantity, triggers item removal instead of decrease.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { min, value } = this.getCurrentValues();

    // At min quantity: trigger removal
    if (value <= min) {
      const { quantityInput } = this.refs;
      const cartLine = Number(quantityInput.dataset.cartLine);
      if (cartLine) {
        this.dispatchEvent(new QuantitySelectorUpdateEvent(0, cartLine));
      }
      return;
    }

    this.updateQuantity(-1);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
